import type { IntegrationProvider } from "@io-spoke/contracts/integrations";
import type { IntegrationConnection } from "../../lib/integrations";

export const setupCopy: Record<
  IntegrationProvider,
  { name: string; description: string }
> = {
  azure_devops: {
    name: "Azure DevOps",
    description:
      "Credentials are encrypted and never shown again after saving.",
  },
  jira: {
    name: "Jira Cloud",
    description:
      "Authorize one Jira Cloud site through Atlassian, then choose how IO Spoke reads and creates work.",
  },
  shortcut: {
    name: "Shortcut",
    description:
      "Connect a Shortcut workspace to synchronize stories and create approved recovery work.",
  },
  monday: {
    name: "monday.com",
    description:
      "Connect a board and map its flexible columns to IO Spoke evidence fields.",
  },
  linear: {
    name: "Linear",
    description:
      "Connect a Linear team to synchronize issues and create approved recovery work.",
  },
  github: {
    name: "GitHub",
    description:
      "Connect one GitHub repository to synchronize issues and create approved recovery work.",
  },
  zendesk: {
    name: "Zendesk",
    description:
      "Authorize one Zendesk account to synchronize support tickets and create approved recovery tickets.",
  },
  hubspot: {
    name: "HubSpot",
    description:
      "Authorize read-only deal access to bring CRM context into customer-risk analysis.",
  },
  intercom: {
    name: "Intercom",
    description:
      "Authorize read-only conversation access to bring support context into rescue analysis.",
  },
  salesforce: {
    name: "Salesforce",
    description:
      "Authorize read-only opportunity access to bring account and renewal context into rescue analysis.",
  },
  slack: {
    name: "Slack",
    description:
      "Connect channel evidence and continue investigations with questions, answers, and shared results.",
  },
};

export const syncProviders = new Set<IntegrationProvider>([
  "jira",
  "shortcut",
  "monday",
  "linear",
  "github",
  "zendesk",
  "hubspot",
  "intercom",
  "salesforce",
  "slack",
]);

export function connectionSummary(connection: IntegrationConnection) {
  const config = connection.configSummary;
  switch (connection.provider) {
    case "jira":
      return `${config.siteUrl} / ${config.projectKey}`;
    case "shortcut":
      return `Workflow ${config.workflowStateId} · ${config.storyType} stories`;
    case "monday":
      return `Board ${config.boardId}${config.groupId ? ` · group ${config.groupId}` : ""}`;
    case "linear":
      return `Team ${config.teamId}`;
    case "github":
      return `${config.owner} / ${config.repository}`;
    case "zendesk":
      return `${config.subdomain}.zendesk.com`;
    case "hubspot":
      return `Account ${config.accountId ?? "connected"} · read-only`;
    case "intercom":
      return `${config.workspaceName ?? "Workspace"} · ${config.region ?? "US"} · read-only`;
    case "salesforce":
      return `${config.environment ?? "production"} workspace ${config.orgId ?? "connected"} · read-only`;
    case "slack":
      return `${config.teamName ?? "Workspace"} / #${config.channelName ?? config.channelId} · ${config.interactive === true ? "Messaging authorized" : "Reconnect to enable conversations"}`;
    case "azure_devops":
      return `${config.organization} / ${config.project}`;
  }
}
